import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SLICE_TYPES } from 'store/constants';
import { RootState } from 'store/store';
import {
    Answer,
    Course,
    CurrentAnswer,
    LearningAnswers,
    MatchTextWithDescriptionCurrentAnswer,
    TaskType,
} from 'types/elearning';

type NewAnswer = { questionId: string; newAnswerId: string };
type TasksCompletion = Record<number, boolean>;
type TaskAnswerData = {
    isDone: boolean;
    userAnswer: Record<string, string[]>;
    correctAnswer?: Record<string, string[]>;
    points?: {
      obtained?: number; // opcjonalne
      total?: number;    // opcjonalne
    };
    descriptionAnswer: string | null;
  };
  
type TasksAnswers = Record<number, TaskAnswerData>;

type InitialState = {
    canSubmitAnswers: boolean;
    checkedAnswers: CurrentAnswer;
    learningAnswers: LearningAnswers,
    correctAnswer: Answer['correct_answer'];
    isAnswerChecked: boolean;
    isSuccessSendAnswer: boolean;
    courseData: Course;
    isQuestionDone: boolean;
    answer: Answer;
    curentDnDOrder: string[];
    matchTextWithDescriptionCurrentAnswer: MatchTextWithDescriptionCurrentAnswer;
    taskType: TaskType;
    tasksCompletion: TasksCompletion;
    tasksSuccess: Record<number, boolean>;
    tasksAnswers: TasksAnswers;
};

const initialState: InitialState = {
    canSubmitAnswers: false,
    checkedAnswers: null,
    learningAnswers: {},
    correctAnswer: null,
    isAnswerChecked: false,
    isSuccessSendAnswer: false,
    courseData: null,
    isQuestionDone: false,
    answer: null,
    curentDnDOrder: [],
    matchTextWithDescriptionCurrentAnswer: null,
    taskType: null,
    tasksCompletion: {},
    tasksSuccess: {},
    tasksAnswers: {}
};

export const topicSlice = createSlice({
    name: SLICE_TYPES.TOPIC,
    initialState,
    reducers: {
        setCanSubmitAnswer: (state, action) => {
            state.canSubmitAnswers = action.payload;
        },
        setCheckedAnswerSingle: (state, action: PayloadAction<NewAnswer>) => {
            const newCheckedAnswersSingl = {
                ...state.checkedAnswers,
                [action.payload.questionId]: [action.payload.newAnswerId],
            };

            state.checkedAnswers = newCheckedAnswersSingl;
        },
        setCheckedAnswerMultiple: (state, action: PayloadAction<NewAnswer>) => {
            const questionId = action.payload.questionId;
            const newAnswerId = action.payload.newAnswerId;
            const questionToChange = state.checkedAnswers?.[questionId];

            if (questionToChange?.includes(newAnswerId)) {
                state.checkedAnswers = {
                    ...state.checkedAnswers,
                    [questionId]: questionToChange?.filter?.(
                        (answer) => answer !== newAnswerId
                    ),
                };
            } else {
                const newCheckedAnswersMultiple = {
                    ...state.checkedAnswers,
                    [questionId]: [...(questionToChange || []), newAnswerId],
                };

                state.checkedAnswers = newCheckedAnswersMultiple;
            }
        },
        resetCheckedAnswer: (state) => {
            state.checkedAnswers = null;
        },
        setCorrectAnswer: (
            state,
            action: PayloadAction<Answer['correct_answer']>
        ) => {
            state.correctAnswer = action.payload;
        },
        setIsAnswerChecked: (state, action: PayloadAction<boolean>) => {
            state.isAnswerChecked = action.payload;
        },
        resetSlice: () => initialState,
        resetSliceWithoutCourseData: (state) => {
            const newState = { ...initialState };
            delete newState.courseData;
            return { ...state, ...newState };
        },
        setIsSuccessSendAnswer: (state, action: PayloadAction<boolean>) => {
            state.isSuccessSendAnswer = action.payload;
        },
        setCourseData: (state, action: PayloadAction<Course>) => {
            state.courseData = action.payload;
        },
        setIsQuestionDone: (state, action: PayloadAction<boolean>) => {
            state.isQuestionDone = action.payload;
        },
        setAnswer: (state, action: PayloadAction<Answer>) => {
            state.answer = action.payload;
        },
        setCurentDnDOrder: (state, action: PayloadAction<string[]>) => {
            state.curentDnDOrder = action.payload;
        },
        setMatchTextWithDescriptionCurrentAnswer: (
            state,
            action: PayloadAction<MatchTextWithDescriptionCurrentAnswer>
        ) => {
            state.matchTextWithDescriptionCurrentAnswer = {
                ...state.matchTextWithDescriptionCurrentAnswer,
                ...action.payload,
            };
        },
        setOneMatchTextWithDescriptionCurrentAnswer: (
            state,
            action: PayloadAction<{
                dropzoneId: string;
                elementId: string;
                removeFromPreviousPosition: boolean;
            }>
        ) => {
            const elementId = action.payload.elementId;
            let newState = { ...state.matchTextWithDescriptionCurrentAnswer };

            if (action.payload.removeFromPreviousPosition) {
                const prevElement = Object.entries(newState).find(
                    ([, image]) => image === elementId
                );
                newState = { ...newState, [prevElement[0]]: '' };
            }

            newState = {
                ...newState,
                [action.payload.dropzoneId]: action.payload.elementId,
            };

            state.matchTextWithDescriptionCurrentAnswer = newState;
        },

        setTaskType: (state, action: PayloadAction<TaskType>) => {
            state.taskType = action.payload;
        },

        setLearningAnswer: (
            state,
            action: PayloadAction<{
                taskId: number | string;
                questionId: string;
                answerId: string | string[];
            }>
        ) => {
            const { taskId, questionId, answerId } = action.payload;
        
            const answersArray = Array.isArray(answerId) ? answerId : [answerId];
        
            state.learningAnswers = {
                ...state.learningAnswers,
                [taskId]: {
                    ...(state.learningAnswers?.[taskId] || {}),
                    [questionId]: answersArray,
                },
            };
        },
        
        resetLearningAnswers: (state) => {
            state.learningAnswers = {};
        },
        
        resetLearningAnswersByTask: (
            state,
            action: PayloadAction<number | string>
        ) => {
            const taskId = action.payload;
            const { [taskId]: _, ...rest } = state.learningAnswers;
            state.learningAnswers = rest;
        },
        setTasksCompletion: (state, action: PayloadAction<Record<number, boolean>>) => {
            state.tasksCompletion = {
                ...state.tasksCompletion,
                ...action.payload,
            };
        },
        setTaskSuccess: (state, action: PayloadAction<{ taskId: number; success: boolean }>) => {
            state.tasksSuccess = {
                ...state.tasksSuccess,
                [action.payload.taskId]: action.payload.success,
            };
        },
        setTaskAnswer: (
            state,
            action: PayloadAction<{ taskId: number; data: Answer }>
        ) => {
            const { taskId, data } = action.payload;
            state.tasksAnswers = {
              ...state.tasksAnswers,
              [taskId]: {
                isDone: data.is_done,
                userAnswer: data.user_answer || {},
                correctAnswer: data.correct_answer,
                points: data.points,
                descriptionAnswer: data?.success_info || data?.fail_info || null,
              },
            };
        },
        setTaskAnswerReset: (state, action: PayloadAction<number>) => {
            const taskId = action.payload;
            const { [taskId]: _, ...rest } = state.tasksAnswers;
            state.tasksAnswers = rest;
            delete state.tasksCompletion[taskId];
            delete state.tasksSuccess[taskId];
        },
        setMatchTextWithDescriptionCurrentAnswerReset: (
            state,
            action: PayloadAction<{ keys: string[] }>
        ) => {
            const { keys } = action.payload;
            state.matchTextWithDescriptionCurrentAnswer = keys.reduce(
                (acc, key) => ({ ...acc, [key]: '' }),
                {}
            );
        },

        setLearningAnswersForTaskReset: (state, action: PayloadAction<number | string>) => {
            const taskId = action.payload;
            if (state.learningAnswers?.[taskId]) {
                state.learningAnswers[taskId] = {}; // albo null/{} w zależności od struktury
            }
        },
    },
});

export const {
    setCanSubmitAnswer,
    setCheckedAnswerSingle,
    setCheckedAnswerMultiple,
    resetCheckedAnswer,
    setCorrectAnswer,
    setIsAnswerChecked,
    resetSlice,
    resetSliceWithoutCourseData,
    setIsSuccessSendAnswer,
    setCourseData,
    setIsQuestionDone,
    setAnswer,
    setCurentDnDOrder,
    setMatchTextWithDescriptionCurrentAnswer,
    setOneMatchTextWithDescriptionCurrentAnswer,
    setTaskType,
    setLearningAnswer,
    resetLearningAnswers,
    setTasksCompletion,
    setTaskSuccess,
    setTaskAnswer,
    setTaskAnswerReset,
    setMatchTextWithDescriptionCurrentAnswerReset,
    setLearningAnswersForTaskReset
} = topicSlice.actions;

export const topicSliceReducer = topicSlice.reducer;

export const selectCanSubmitAnswer = (state: RootState) =>
    state.topic.canSubmitAnswers;

export const selectCheckedAnswers = (state: RootState) =>
    state.topic.checkedAnswers;

export const selectLearningAnswers  = (state: RootState) =>
    state.topic.learningAnswers;

export const selectLearningAnswerById = (questionId: number) => (state: RootState) =>
    state.topic.learningAnswers?.[questionId];

export const selectIsQuestionAnswered = (taskId: number | string) => (state: RootState) => {
    const taskAnswers = state.topic.learningAnswers?.[taskId];
    if (!taskAnswers) return false;
  
    return Object.values(taskAnswers).some(
      (answers) => Array.isArray(answers) && answers.length > 0
    );
  };

export const selectCorrectAnswer = (state: RootState) =>
    state.topic.correctAnswer;

export const selectCorrectAnswerByTaskId = (taskId: number) => (state: RootState) =>
    state.topic.tasksAnswers?.[taskId]?.correctAnswer;

export const selectIsAnswerChecked = (state: RootState) =>
    state.topic.isAnswerChecked;

export const selectIsSuccessSendAnswer = (state: RootState) =>
    state.topic.isSuccessSendAnswer;

export const selectCourseData = (state: RootState) => state.topic.courseData;

export const selectisQuestionDone = (state: RootState) =>
    state.topic.isQuestionDone;

export const selectAnswer = (state: RootState) => state.topic.answer;

export const selectCurentDnDOrder = (state: RootState) =>
    state.topic.curentDnDOrder;

export const selectMatchTextWithDescriptionCurrentAnswer = (state: RootState) =>
    state.topic.matchTextWithDescriptionCurrentAnswer;

export const selectTaskType = (state: RootState) => state.topic.taskType;

export const selectIsTaskSuccess = (taskId: number) => (state: RootState) =>
    !!state.topic.tasksSuccess?.[taskId];

export const selectTaskAnswerById = (taskId: number) => (state: RootState) =>
    state.topic.tasksAnswers?.[taskId];

export const selectIsTaskChecked = (taskId: number) => (state: RootState) =>
    !!state.topic.tasksAnswers?.[taskId];

export const selectIsAnyTaskIncomplete = (state: RootState) => {
    return Object.values(state.topic.tasksCompletion).some(done => !done);
};